import React, { useState } from 'react';
import {
  approveMemberWithoutGuestLink,
  rejectAccountLinkViaJoinRequest,
} from '../../services/guestAccountLinkingService';
import AssignGuestEmailModal from './AssignGuestEmailModal';
import NoGuestPlayerModal from './NoGuestPlayerModal';
import RejectJoinRequestModal from './RejectJoinRequestModal';
import Button from '../common/Button';
import { Text, Typography } from '../common/Typography';
import { StatusBadge } from '../common';
import PlayerAvatar from '../common/PlayerAvatar';

const AccountLinkRequestCard = ({ request, clubId, currentUser, onResolved }) => {
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showNoGuestModal, setShowNoGuestModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [resolvedMessage, setResolvedMessage] = useState(null);

  const handleApproveWithoutLink = async () => {
    setProcessing(true);
    setError(null);

    try {
      await approveMemberWithoutGuestLink(
        clubId,
        request.user_id,
        currentUser.id,
        currentUser.id
      );
      setShowNoGuestModal(false);
      setResolvedMessage(`${request.user_name} has been added to the club.`);
      onResolved?.(request.user_id);
    } catch (err) {
      console.error('[AccountLinkRequestCard] Approve failed:', err);
      setError(err.message || 'Failed to approve join');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (rejectionData) => {
    setProcessing(true);
    setError(null);

    try {
      await rejectAccountLinkViaJoinRequest(
        clubId,
        request.user_id,
        currentUser.id,
        currentUser.id,
        rejectionData
      );
      setShowRejectModal(false);
      setShowNoGuestModal(false);
      setResolvedMessage(`Join request from ${request.user_name} was rejected.`);
      onResolved?.(request.user_id);
    } catch (err) {
      console.error('[AccountLinkRequestCard] Reject failed:', err);
      setError(err.message || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  if (resolvedMessage) {
    return (
      <div className="rounded-lg border border-success/30 bg-success-light/30 p-4">
        <Text className="text-sm text-success-dark">{resolvedMessage}</Text>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-border-medium bg-surface-subtle p-4">
        <div className="mb-3 flex items-center gap-3">
          <PlayerAvatar
            player={{
              name: request.user_name,
              first_name: request.user_first_name,
              last_name: request.user_last_name,
              gender: request.user_gender,
              avatar_url: request.user_avatar_url,
            }}
            size="lg"
          />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Typography variant="strong">{request.user_name}</Typography>
              <StatusBadge variant="info" size="small">Account link</StatusBadge>
            </div>
          </div>
        </div>

        <p className="mb-1 text-sm text-text-secondary">{request.user_email}</p>
        <p className="mb-4 text-xs text-text-muted">
          {new Date(request.created_at).toLocaleDateString()}
        </p>

        {error && (
          <div className="mb-3 rounded-lg border border-danger/30 bg-danger-light/30 px-3 py-2 text-sm text-danger-dark">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="primary"
            size="small"
            className="sm:flex-1"
            onClick={() => setShowAssignModal(true)}
            disabled={processing}
          >
            Assign email to guest account
          </Button>
          <Button
            variant="outline"
            size="small"
            className="sm:flex-1"
            onClick={() => setShowNoGuestModal(true)}
            disabled={processing}
          >
            No guest player
          </Button>
        </div>
      </div>

      <AssignGuestEmailModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        request={request}
        clubId={clubId}
        currentUser={currentUser}
        onAssigned={() => {
          setResolvedMessage(`${request.user_name} will be notified to link their account.`);
          onResolved?.(request.user_id);
        }}
      />

      <NoGuestPlayerModal
        isOpen={showNoGuestModal}
        onClose={() => !processing && setShowNoGuestModal(false)}
        playerName={request.user_name}
        loading={processing}
        onApproveWithoutLink={handleApproveWithoutLink}
        onReject={() => {
          setShowNoGuestModal(false);
          setShowRejectModal(true);
        }}
      />

      <RejectJoinRequestModal
        isOpen={showRejectModal}
        onClose={() => !processing && setShowRejectModal(false)}
        onConfirm={handleReject}
        playerName={request.user_name}
        loading={processing}
      />
    </>
  );
};

export default AccountLinkRequestCard;
